/**
 * Spare Parts Master tab — manages the catalogue of canonical spare parts
 * used by the Service Ops modal's add-part autocomplete.
 *
 * Renders under the Service Packages page as a sibling tab.
 */
import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { sparePartsMasterApi } from "../../services/api";
import { formatCurrency } from "../../utils/currency";

const empty = () => ({
  name: "",
  brand: "",
  hsn_code: "8708",
  default_price: "",
  country_codes: ["IN"],
  makes: [],
  car_make: "",
  car_model: "",
  labor_amount: "",
  gst_pct: 18,
});

export default function SparePartsMasterTab({ user }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null); // null | newDoc | existing

  const fetchRows = async () => {
    setLoading(true);
    try {
      const { data } = await sparePartsMasterApi.list({
        q: q || undefined,
        include_inactive: showInactive,
        page_size: 100,
      });
      setRows(data?.items || []);
      setTotal(data?.total || 0);
    } catch (e) {
      console.error("[SparePartsMasterTab] list failed:", e);
      toast.error("Failed to load spare parts master");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const t = setTimeout(fetchRows, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, showInactive]);

  const onSave = async () => {
    if (!editing?.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      const payload = {
        ...editing,
        default_price: editing.default_price ? Number(editing.default_price) : null,
        labor_amount: editing.labor_amount !== "" && editing.labor_amount != null
          ? Number(editing.labor_amount) : null,
        gst_pct: editing.gst_pct !== "" && editing.gst_pct != null
          ? Number(editing.gst_pct) : 18,
        makes: (editing.makes || []).map((m) => ({
          ...m,
          default_price: m.default_price ? Number(m.default_price) : 0,
        })),
      };
      if (editing.id) {
        await sparePartsMasterApi.update(editing.id, payload);
        toast.success("Updated");
      } else {
        await sparePartsMasterApi.create(payload);
        toast.success("Created");
      }
      setEditing(null);
      fetchRows();
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e?.message));
    }
  };

  const onDelete = async (id) => {
    if (!confirm("Archive this part from the master?")) return;
    try {
      await sparePartsMasterApi.delete(id);
      toast.success("Archived");
      fetchRows();
    } catch (e) {
      toast.error("Delete failed: " + (e?.response?.data?.detail || e?.message));
    }
  };

  return (
    <div data-testid="spm-tab">
      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or brand..."
            className="pl-9"
            data-testid="spm-search"
          />
        </div>
        <label className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            data-testid="spm-show-inactive"
          />
          Show archived
        </label>
        <Button
          onClick={() => setEditing(empty())}
          className="gap-2 bg-orange-600 hover:bg-orange-700"
          data-testid="spm-create-btn"
        >
          <Plus className="h-4 w-4" /> New Spare Part
        </Button>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border overflow-hidden" data-testid="spm-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Brand</th>
              <th className="px-3 py-2 text-left">Car Make</th>
              <th className="px-3 py-2 text-left">Car Model</th>
              <th className="px-3 py-2 text-left">HSN</th>
              <th className="px-3 py-2 text-right">Price</th>
              <th className="px-3 py-2 text-right">Labor</th>
              <th className="px-3 py-2 text-center">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="text-center py-6 text-slate-400">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center py-6 text-slate-400 italic">
                  No spare parts in the master.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.id}
                className={`border-t hover:bg-slate-50 ${
                  r.is_active === false ? "opacity-60" : ""
                }`}
                data-testid={`spm-row-${r.id}`}
              >
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2">{r.brand || "—"}</td>
                <td className="px-3 py-2 text-slate-700">{r.car_make || "—"}</td>
                <td className="px-3 py-2 text-slate-700">{r.car_model || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.hsn_code || "—"}</td>
                <td className="px-3 py-2 text-right">
                  {r.default_price ? formatCurrency(r.default_price, user) : "—"}
                </td>
                <td className="px-3 py-2 text-right text-amber-700">
                  {r.labor_amount ? formatCurrency(r.labor_amount, user) : "—"}
                </td>
                <td className="px-3 py-2 text-center text-xs">
                  {r.is_active === false ? (
                    <span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                      Archived
                    </span>
                  ) : r.auto_learned ? (
                    <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                      Auto-learned
                    </span>
                  ) : (
                    <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
                      Active
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing(r)}
                    data-testid={`spm-edit-${r.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {r.is_active !== false && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(r.id)}
                      data-testid={`spm-delete-${r.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-slate-500 text-right mt-2">{total} total</div>

      {/* Editor */}
      {editing && (
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="!max-w-2xl" data-testid="spm-editor">
            <DialogHeader>
              <DialogTitle>
                {editing.id ? "Edit Spare Part" : "New Spare Part"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Name *</Label>
                  <Input
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    placeholder="e.g. Brake Pad (Front)"
                    data-testid="spm-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Brand</Label>
                  <Input
                    value={editing.brand || ""}
                    onChange={(e) => setEditing({ ...editing, brand: e.target.value })}
                    placeholder="Bosch / OEM / etc."
                    data-testid="spm-brand"
                  />
                </div>
                <div>
                  <Label className="text-xs">HSN Code</Label>
                  <Input
                    value={editing.hsn_code || ""}
                    onChange={(e) => setEditing({ ...editing, hsn_code: e.target.value })}
                    placeholder="8708"
                    data-testid="spm-hsn"
                  />
                </div>
                <div>
                  <Label className="text-xs">
                    Default Price (used when (make,model) not matched)
                  </Label>
                  <Input
                    type="number"
                    value={editing.default_price ?? ""}
                    onChange={(e) =>
                      setEditing({ ...editing, default_price: e.target.value })
                    }
                    placeholder="0"
                    data-testid="spm-default-price"
                  />
                </div>
                <div>
                  <Label className="text-xs">Car Make</Label>
                  <Input
                    value={editing.car_make || ""}
                    onChange={(e) => setEditing({ ...editing, car_make: e.target.value })}
                    placeholder="e.g. Maruti Suzuki"
                    data-testid="spm-car-make"
                  />
                </div>
                <div>
                  <Label className="text-xs">Car Model</Label>
                  <Input
                    value={editing.car_model || ""}
                    onChange={(e) => setEditing({ ...editing, car_model: e.target.value })}
                    placeholder="e.g. Swift"
                    data-testid="spm-car-model"
                  />
                </div>
                <div>
                  <Label className="text-xs">Labor Amount ({activeCurrencySymbol()})</Label>
                  <Input
                    type="number"
                    value={editing.labor_amount ?? ""}
                    onChange={(e) => setEditing({ ...editing, labor_amount: e.target.value })}
                    placeholder="300"
                    data-testid="spm-labor-amount"
                  />
                </div>
                <div>
                  <Label className="text-xs">GST %</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={editing.gst_pct ?? 18}
                    onChange={(e) => setEditing({ ...editing, gst_pct: e.target.value })}
                    placeholder="18"
                    data-testid="spm-gst-pct"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Countries (comma-separated)</Label>
                  <Input
                    value={(editing.country_codes || []).join(", ")}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        country_codes: e.target.value
                          .split(",")
                          .map((s) => s.trim().toUpperCase())
                          .filter(Boolean),
                      })
                    }
                    placeholder="IN, TH"
                    data-testid="spm-countries"
                  />
                </div>
              </div>

              <div className="border rounded p-3 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold">
                    Per-(Brand, Model) Pricing
                  </Label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setEditing({
                        ...editing,
                        makes: [
                          ...(editing.makes || []),
                          { make: "", model: "", default_price: "" },
                        ],
                      })
                    }
                    data-testid="spm-add-make"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Row
                  </Button>
                </div>
                {(editing.makes || []).length === 0 && (
                  <div className="text-xs text-slate-400 italic">
                    Optional. Leave empty if a single default price applies to all
                    vehicles.
                  </div>
                )}
                {(editing.makes || []).map((m, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 mb-1">
                    <Input
                      className="col-span-4"
                      placeholder="Make (e.g. Maruti)"
                      value={m.make}
                      onChange={(e) => {
                        const copy = [...editing.makes];
                        copy[i] = { ...copy[i], make: e.target.value };
                        setEditing({ ...editing, makes: copy });
                      }}
                    />
                    <Input
                      className="col-span-4"
                      placeholder="Model (e.g. Swift)"
                      value={m.model || ""}
                      onChange={(e) => {
                        const copy = [...editing.makes];
                        copy[i] = { ...copy[i], model: e.target.value };
                        setEditing({ ...editing, makes: copy });
                      }}
                    />
                    <Input
                      type="number"
                      className="col-span-3"
                      placeholder="Price"
                      value={m.default_price}
                      onChange={(e) => {
                        const copy = [...editing.makes];
                        copy[i] = { ...copy[i], default_price: e.target.value };
                        setEditing({ ...editing, makes: copy });
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="col-span-1"
                      onClick={() =>
                        setEditing({
                          ...editing,
                          makes: editing.makes.filter((_, j) => j !== i),
                        })
                      }
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button onClick={onSave} data-testid="spm-save">
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
import { activeCurrencySymbol } from '@/utils/currency';
